/** @jsxImportSource @emotion/react */
import type {
    GetStaticPropsContext,
    InferGetStaticPropsType,
    NextPage,
    PreviewData,
} from "next";
import { ParsedUrlQuery } from "querystring";

import { EnterpriseHero } from "@/ui/bespoke/enterprise/enterprise_hero";
import { BigStats } from "@/ui/organisms/big_stats";
import { ClassicTestimonial } from "@/ui/organisms/classic_testimonial";
import { CustomerLogoReel } from "@/ui/organisms/customer_logo_reel";
import { FaqSection } from "@/ui/organisms/faq_section";
import { LogoList } from "@/ui/organisms/logo_list";
import { PrivacyList } from "@/ui/organisms/privacy_list";

import { generatePageLevelProps } from "@/util/data_util";
import { getEnterprisePage, getGlobals } from "@/util/strapi_util";
import { getSectionGap } from "@/util/tokens/layout_util";

export const getStaticProps = async ({
    preview: previewMode,
}: GetStaticPropsContext<ParsedUrlQuery, PreviewData>) => {
    const slug = "enterprise";

    const globalsData = await getGlobals(previewMode);
    const pageData = await getEnterprisePage(previewMode);

    return {
        props: {
            ...generatePageLevelProps({ previewMode, slug }),
            globalsData,
            legacyGrid: false,
            pageData,
            renderNewNavigationBar: true,
            renderNotifications: true,
            renderPostFooter: true,
        },
    };
};

type PageProps = InferGetStaticPropsType<typeof getStaticProps>;

const Page: NextPage<PageProps> = (props) => {
    /**
     * Elements
     */
    const renderPageBody = () => {
        return props.pageData.Page_Body.map((pageBody) => {
            const key = pageBody.id;
            switch (pageBody.__component) {
                case "sections.section-big-stats":
                    return <BigStats {...pageBody} key={key} />;
                case "sections.section-customer-logo-reel":
                    return <CustomerLogoReel {...pageBody} key={key} />;
                case "sections.section-fa-qs":
                    return (
                        <FaqSection
                            {...pageBody}
                            headlineFontSize="HeadlineDefault"
                            key={key}
                            marginBottom="DefaultSectionGap"
                        />
                    );
                case "sections.section-logo-list":
                    return <LogoList {...pageBody} key={key} />;
                case "sections.section-privacy":
                    return <PrivacyList {...pageBody} key={key} />;
                case "sections.section-testimonial":
                    return (
                        <ClassicTestimonial
                            {...pageBody}
                            key={key}
                            sectionGap={getSectionGap(pageBody.Section_Gap)}
                        />
                    );
                default:
                    return null;
            }
        });
    };

    return (
        <>
            <EnterpriseHero {...props.pageData.Hero} />

            {renderPageBody()}
        </>
    );
};

export default Page;
